import * as z from "zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { catchError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { settingsRepo } from "@/lib/repositories";
import { useSettingsStore } from "../stores";
import useAuth from "@/features/auth/hooks/useAuth";

const SecuritySetting = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const saveTrigger = useSettingsStore((s) => s.saveTrigger);
  const setIsSaving = useSettingsStore((s) => s.setIsSaving);

  const securitySchema = z
    .object({
      old_password: z.string().min(1),
      new_password: z.string().min(8),
      confirm_new_password: z.string().min(1),
    })
    .refine((data) => data.new_password === data.confirm_new_password, {
      path: ["confirm_new_password"],
      message: t("errors.password_not_matching"),
    });

  const form = useForm<z.infer<typeof securitySchema>>({
    resolver: zodResolver(securitySchema),
    defaultValues: {
      old_password: "",
      new_password: "",
      confirm_new_password: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof securitySchema>) => {
    setIsSaving(true);
    const [error] = await catchError(settingsRepo.changePassword(data));
    if (error) {
      toast.error(handleApiError(error).message);
    } else {
      toast.success(t("settings.security.save_success"));
      form.reset();
      await logout();
    }
    setIsSaving(false);
  };

  useEffect(() => {
    if (saveTrigger === 0) return;
    const values = form.getValues();
    const allEmpty =
      !values.old_password &&
      !values.new_password &&
      !values.confirm_new_password;
    if (allEmpty) return;
    form.handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTrigger]);

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-sm font-medium">
        <ShieldCheck className="size-4" />
        {t("settings.security.title")}
      </Label>
      <FieldGroup className="gap-y-4">
        <Controller
          name="old_password"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="old-password-input">
                {t("settings.security.current_password")}
              </FieldLabel>
              <Input
                {...field}
                id="old-password-input"
                type="password"
                aria-invalid={fieldState.invalid}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
        <Controller
          name="new_password"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="new-password-input">
                {t("settings.security.new_password")}
              </FieldLabel>
              <Input
                {...field}
                id="new-password-input"
                type="password"
                aria-invalid={fieldState.invalid}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
        <Controller
          name="confirm_new_password"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="confirm-password-input">
                {t("settings.security.confirm_new_password")}
              </FieldLabel>
              <Input
                {...field}
                id="confirm-password-input"
                type="password"
                aria-invalid={fieldState.invalid}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>
    </div>
  );
};

export default SecuritySetting;
